/** Phân loại kết quả sau receive — thuần, test được, không phụ thuộc React/toast. */

export type ReceiveOutcome =
  | "success" // server xác nhận đã đọc lại status=imported
  | "unverified-refresh" // không đọc lại được → không kết luận
  | "unverified-request" // request lỗi (có thể đã ghi một phần) → không kết luận rollback
  | "incomplete" // server báo còn dòng chưa ghi lô → pending là ĐÚNG, không phải tranh chấp
  | "conflict"; // receive ok, không báo thiếu, nhưng server chưa ở trạng thái imported

/**
 * Cảnh báo phụ từ server (vd nhiều giá vốn cùng đơn) — chỉ để hiển thị.
 * Không tham gia phân loại thành công/thất bại. Bỏ rỗng, dedupe giữ thứ tự.
 */
export function uniqueReceiveWarnings(warnings?: readonly string[] | null): string[] {
  if (!Array.isArray(warnings)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of warnings) {
    const s = typeof w === "string" ? w.trim() : "";
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

/** null khi không có cảnh báo → UI giữ nguyên hành vi cũ. */
export function receiveWarningMessage(warnings?: readonly string[] | null): string | null {
  const list = uniqueReceiveWarnings(warnings);
  if (!list.length) return null;
  return `Đã nhập kho, nhưng: ${list.slice(0, 2).join("; ")}${list.length > 2 ? `… (+${list.length - 2})` : ""}`;
}

/**
 * Cảnh báo chỉ hiện khi lần receive này thực sự trọn vẹn (ok, không incomplete, không còn dòng).
 * Receive lỗi/thiếu → không hiện cảnh báo (đã có thông báo lỗi riêng), tránh che mất lỗi.
 */
export function receiveWarningForResult(input: {
  ok?: boolean;
  incomplete?: boolean;
  remainingCount: number;
  warnings?: readonly string[] | null;
}): string | null {
  if (input.ok === false || input.incomplete || input.remainingCount > 0) return null;
  return receiveWarningMessage(input.warnings);
}

export function classifyReceiveOutcome(input: {
  receiveError: string | null;
  refreshError: string | null;
  incomplete: boolean;
  /** status đọc lại từ server; null = không tìm thấy trong danh sách đang lọc. */
  freshStatus: string | null;
  /** read lại được nhưng server báo còn dòng chưa ghi lô. */
  freshIncomplete?: boolean;
}): ReceiveOutcome {
  // Không đọc lại được → ưu tiên "chưa xác minh", kể cả khi response báo lỗi.
  if (input.refreshError) return "unverified-refresh";
  if (input.receiveError) return "unverified-request";
  const incomplete = input.incomplete || !!input.freshIncomplete;
  if (!incomplete && input.freshStatus === "imported") return "success";
  // Còn dòng chưa ghi lô là kết quả mong đợi (đơn vẫn pending để nhập tiếp), KHÔNG phải
  // "đang được cập nhật đồng thời". Chỉ báo conflict khi server không nói thiếu mà vẫn chưa imported.
  if (incomplete) return "incomplete";
  return "conflict";
}
